import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

import { LinkContext } from "../../LinkProvider";
import AnimatedCTA from "../AnimatedCTA";
import bgAircraft from "../../img/home/banner-aircraft.webp";
import bgGrid from "../../img/home/banner-grid.webp";

const GLASS = "bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] backdrop-blur-[5px]";
const DARK = "bg-black";
const ORANGE = "bg-[#ee5414]";
const REVEAL_COLS = 5;
const REVEAL_ROWS = 4;

const REVEAL_TILES = Array.from({ length: REVEAL_COLS * REVEAL_ROWS }, (_, index) => {
  const col = index % REVEAL_COLS;
  const row = Math.floor(index / REVEAL_COLS);
  const order = (index * 37) % (REVEAL_COLS * REVEAL_ROWS);
  const horizontal = (index + row) % 2 === 0;

  return {
    id: index,
    className: horizontal ? "banner-reveal-x" : "banner-reveal-y",
    style: {
      left: `${(col / REVEAL_COLS) * 100}%`,
      top: `${(row / REVEAL_ROWS) * 100}%`,
      width: `${100 / REVEAL_COLS}%`,
      height: `${100 / REVEAL_ROWS}%`,
      animationDelay: `${80 + order * 60}ms`,
      transformOrigin: horizontal
        ? ((index + row) % 2 === 0 ? "left" : "right")
        : ((index + col) % 2 === 0 ? "top" : "bottom"),
    },
  };
});

function StatCardDesktop({ value, label, variant, cls }) {
  const bg = variant === "orange" ? ORANGE : variant === "dark" ? DARK : GLASS;
  const radius = variant === "glass" ? "rounded-[16px]" : "rounded-[20px]";

  return (
    <div className={`hidden xl:flex absolute ${bg} ${radius} w-[244px] h-[186px] pt-[36px] px-[38px] pb-[48px] flex-col items-start gap-[10px] ${cls}`}>
      <span className="font-['Montserrat'] font-extrabold text-[64px] text-white leading-[1.05] tracking-[-0.4px] whitespace-nowrap">
        {value}
      </span>
      <span className="font-['Montserrat'] font-medium text-[16px] text-white leading-[1.2]">
        {label}
      </span>
    </div>
  );
}

function StatCardMobile({ value, label, variant, cls, w }) {
  const bg = variant === "orange" ? ORANGE : variant === "dark" ? DARK : GLASS;
  const radius = (variant === "dark" || variant === "orange") ? "rounded-[20px]" : "rounded-[16px]";

  return (
    <div className={`absolute ${bg} ${radius} ${w} h-[80px] flex flex-col justify-center gap-[6px] px-[20px] ${cls}`}>
      <span className="font-['Montserrat'] font-extrabold text-[24px] text-white leading-[1.2] tracking-[-0.4px] whitespace-nowrap">
        {value}
      </span>
      <span className="font-['Montserrat'] font-medium text-[12px] text-white leading-[1.2]">
        {label}
      </span>
    </div>
  );
}

function Banner() {
  const { t } = useTranslation();
  const { link } = useContext(LinkContext);

  return (
    <section className="relative bg-[#020202] overflow-hidden pt-[56px] xl:pt-[68px] xl:min-h-[812px]">
      {/* Desktop-only backgrounds */}
      <img
        src={bgAircraft}
        alt=""
        aria-hidden
        className="hidden xl:block absolute inset-0 z-0 w-full h-full object-cover select-none pointer-events-none"
      />
      <img
        src={bgGrid}
        alt=""
        aria-hidden
        className="hidden xl:block absolute inset-0 z-0 w-full h-full object-cover select-none pointer-events-none opacity-70"
      />
      <div
        className="hidden xl:block absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            "linear-gradient(to right, #020202 0%, rgba(2,2,2,0.75) 38%, rgba(2,2,2,0.25) 65%, rgba(2,2,2,0.55) 100%)",
        }}
      />
      <div className="banner-grid-lines hidden xl:block absolute inset-0 z-[2] pointer-events-none" />
      <div className="hidden xl:block absolute top-[106px] left-1/2 z-[3] w-full max-w-[1220px] h-[744px] -translate-x-1/2 pointer-events-none overflow-hidden">
        {REVEAL_TILES.map((tile) => (
          <span
            key={tile.id}
            className={`banner-reveal-tile ${tile.className}`}
            style={tile.style}
          />
        ))}
      </div>

      <div className="relative z-[4] max-w-[1220px] mx-auto xl:min-h-[830px]">
        {/* Desktop title */}
        <div className="hidden xl:flex absolute top-[140px] flex-col z-[4]">
          <div className="flex flex-col tracking-[-1.28px] max-w-[640px]">
            <h1 className="font-['Montserrat'] font-black text-[54px] text-[#e1e1e1]">
              {t("home.banner.line1")}
            </h1>
            <h1 className="font-['Montserrat'] font-black text-[72px] text-[#ee5414] leading-[1.1]">
              {t("home.banner.line2")}
            </h1>
          </div>
          <p className="mt-[34px] font-['Inter'] font-medium text-[12px] text-[#acacac] tracking-[1.4px] uppercase leading-[30px]">
            {t("home.banner.subtitle")}
          </p>
        </div>

        <StatCardDesktop
          value={t("home.banner.stat1.value")}
          label={t("home.banner.stat1.label")}
          variant="dark"
          cls="left-[976px] top-[38px] z-[4]"
        />
        <StatCardDesktop
          value={t("home.banner.stat2.value")}
          label={t("home.banner.stat2.label")}
          variant="glass"
          cls="left-[976px] top-[224px] z-[4]"
        />
        <StatCardDesktop
          value={t("home.banner.stat3.value")}
          label={t("home.banner.stat3.label")}
          variant="glass"
          cls="left-[488px] top-[411px] z-[4]"
        />
        <StatCardDesktop
          value={t("home.banner.stat4.value")}
          label={t("home.banner.stat4.label")}
          variant="orange"
          cls="left-0 top-[596px] z-[4]"
        />
        <StatCardDesktop
          value={t("home.banner.stat5.value")}
          label={t("home.banner.stat5.label")}
          variant="glass"
          cls="left-[244px] top-[596px] z-[4]"
        />

        {/* Desktop desc + CTA */}
        <div className="hidden xl:flex absolute left-[732px] top-[595px] w-[427px] flex-col gap-[30px] z-[4]">
          <p className="font-['Inter'] font-normal text-[13px] text-[#777] leading-[23px]">
            {t("home.banner.desc")}
          </p>
          <div className="flex items-center">
            <AnimatedCTA href={link}>
              {t("btn.start-trading")}
            </AnimatedCTA>
          </div>
        </div>

        {/* Mobile layout */}
        <div className="xl:hidden relative min-h-[909px]">
          {/* Aircraft image in middle section with top+bottom gradient fade */}
          <div className="absolute left-0 top-[185px] w-full h-[519px] overflow-hidden pointer-events-none">
            <img
              src={bgAircraft}
              alt=""
              aria-hidden
              className="absolute w-full h-full object-cover select-none"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, #020202 0%, rgba(2,2,2,0) 26%), linear-gradient(0deg, #020202 0%, rgba(2,2,2,0) 22%)",
              }}
            />
          </div>

          {/* Title + subtitle */}
          <div className="absolute left-[20px] right-[20px] top-[40px] flex flex-col gap-[20px]">
            <div className="flex flex-col font-['Montserrat'] font-bold tracking-[-1.28px]">
              <span className="text-[28px] text-[#e1e1e1] leading-[normal]">
                {t("home.banner.line1")}
              </span>
              <span className="text-[34px] text-[#ee5414] leading-[normal]">
                {t("home.banner.line2")}
              </span>
            </div>
            <span className="font-['Inter'] font-medium text-[12px] text-[#636363] tracking-[1.4px] uppercase leading-[20px]">
              {t("home.banner.subtitle")}
            </span>
          </div>

          {/* 120+ Countries — glass */}
          <StatCardMobile
            value={t("home.banner.stat1.value")}
            label={t("home.banner.stat1.label")}
            variant="glass"
            w="w-[166px]"
            cls="left-[20px] top-[238px]"
          />

          {/* 2.5M+ Traders — dark */}
          <StatCardMobile
            value={t("home.banner.stat2.value")}
            label={t("home.banner.stat2.label")}
            variant="dark"
            w="w-[165px]"
            cls="right-[20px] top-[288px]"
          />

          {/* High Compliance — glass */}
          <StatCardMobile
            value={t("home.banner.stat3.value")}
            label={t("home.banner.stat3.label")}
            variant="glass"
            w="w-[175px]"
            cls="left-[20px] top-[338px]"
          />

          {/* 24/7 Support — orange */}
          <StatCardMobile
            value={t("home.banner.stat4.value")}
            label={t("home.banner.stat4.label")}
            variant="orange"
            w="w-[175px]"
            cls="left-[20px] top-[513px]"
          />

          {/* 250+ Instruments — glass */}
          <StatCardMobile
            value={t("home.banner.stat5.value")}
            label={t("home.banner.stat5.label")}
            variant="glass"
            w="w-[155px]"
            cls="right-[20px] top-[513px]"
          />

          {/* Description + CTA */}
          <div className="absolute left-[20px] right-[20px] top-[633px] flex flex-col gap-[40px]">
            <p className="font-['Inter'] font-normal text-[14px] text-[#777] leading-[1.4]">
              {t("home.banner.desc")}
            </p>
            <AnimatedCTA href={link} mobile full>
              {t("btn.start-trading")}
            </AnimatedCTA>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Banner;
